/**
 * SuppliersDirectory — customer-facing browse/search page at /suppliers.
 *
 * Supports:
 *   - Category pills
 *   - Postcode + radius filter
 *   - Optional event date filter (pre-fills from ?date= query) which
 *     unlocks the "Book Instantly ⚡" CTA for eligible suppliers
 *   - Smart ranking badges (🟢 available on date, ⚡ fast responder,
 *     🏆 highly booked, 🔥 popular)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass, MapPin, ChatCircle, CaretRight, Storefront, CalendarBlank, Lightning, FileText,
} from '@phosphor-icons/react';
import InstantBookingModal from '../components/InstantBookingModal';
import FayreInlinePromo from '../components/FayreInlinePromo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuppliersDirectory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [postcode, setPostcode] = useState(searchParams.get('postcode') || '');
  const [radius, setRadius] = useState(Number(searchParams.get('radius')) || 25);
  const [date, setDate] = useState(searchParams.get('date') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [instantTarget, setInstantTarget] = useState(null); // supplier obj
  const [eventContext, setEventContext] = useState(null); // {event_name, event_date, event_location, event_type_emoji}
  const [fayreSuggestion, setFayreSuggestion] = useState(null);

  // Pull the most relevant upcoming fayre to show as a "Happening soon"
  // banner above the results. Re-uses the homepage feed endpoint and
  // filters in-memory: prefers fayres with the searched category, near
  // the searched postcode if we have one. Single network call, fully
  // best-effort — never blocks results.
  useEffect(() => {
    if (!category) { setFayreSuggestion(null); return; }
    let cancelled = false;
    const anon = (() => { try { return localStorage.getItem('eventdrop_anon_id'); } catch { return null; } })();
    axios.get(`${API}/fayres/upcoming`, { params: { anon_id: anon, limit: 12 } })
      .then((r) => {
        if (cancelled) return;
        const fayres = r.data?.fayres || [];
        // Prefer fayres advertising this category, otherwise fall back
        // to the closest/soonest one returned by the backend.
        const matching = fayres.find((f) => (f.categories || []).includes(category));
        setFayreSuggestion(matching || fayres[0] || null);
      })
      .catch(() => setFayreSuggestion(null));
    return () => { cancelled = true; };
  }, [category, postcode]);

  useEffect(() => {
    axios.get(`${API}/suppliers/categories`)
      .then(r => setCategories(r.data.categories || []))
      .catch(() => {});
  }, []);

  // If we arrived from an event, fetch the event's context for the header
  useEffect(() => {
    const evId = searchParams.get('event_id');
    if (!evId) { setEventContext(null); return; }
    axios.get(`${API}/events/${evId}/context`)
      .then((r) => setEventContext(r.data.event_context))
      .catch(() => setEventContext(null));
  }, [searchParams]);

  const runSearch = useCallback(async (pickedCategory = category) => {
    if (!pickedCategory) { toast.error('Pick a category first'); return; }
    if (!postcode.trim()) { toast.error('Enter a postcode'); return; }
    setLoading(true);
    setHasSearched(true);
    try {
      const params = {
        category: pickedCategory,
        postcode: postcode.trim(),
        radius_miles: radius,
      };
      if (date) params.date = date;
      const r = await axios.get(`${API}/suppliers/search`, { params });
      setResults(r.data.results || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Search failed';
      toast.error(msg);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [category, postcode, radius, date]);

  // Auto-run search when coming in from an event pre-fill
  useEffect(() => {
    if (searchParams.get('category') && searchParams.get('postcode') && !hasSearched) {
      runSearch(searchParams.get('category'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInstant = (e, supplier) => {
    e.stopPropagation();
    e.preventDefault();
    setInstantTarget(supplier);
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="suppliers-directory">
      {/* Header */}
      <div className="border-b border-white/5 px-5 py-6" style={{ background: 'linear-gradient(180deg, rgba(108,85,224,0.12), transparent)' }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              {eventContext ? (
                <div data-testid="event-context-header">
                  <p className="text-[10px] font-black tracking-[2px] uppercase text-[#C29D52]/85 mb-1 flex items-center gap-1.5">
                    <span>{eventContext.event_type_emoji}</span>
                    Suppliers for {eventContext.event_name}
                  </p>
                  <h1 className="text-2xl md:text-3xl font-black leading-tight">
                    {category ? `${categories.find((c) => c.id === category)?.label || 'Suppliers'} for your event` : 'Book your suppliers'}
                  </h1>
                  <p className="text-white/50 text-sm mt-1">
                    {eventContext.event_date ? formatDateShort(eventContext.event_date) : ''}
                    {eventContext.event_postcode ? ` · ${eventContext.event_postcode}` : ''}
                    {eventContext.event_location ? ` · ${eventContext.event_location}` : ''}
                  </p>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl md:text-4xl font-black leading-tight">Find event suppliers</h1>
                  <p className="text-white/50 text-sm mt-1">Photographers, videographers, DJs, florists, caterers, venues, cake makers & more — near your event.</p>
                </>
              )}
            </div>
            <button
              onClick={() => navigate('/supplier/onboard')}
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/15 shrink-0"
              data-testid="join-as-supplier"
            >
              <Storefront size={14} weight="bold" /> Join as a supplier
            </button>
          </div>

          {/* Category pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
            {categories.map(c => (
              <button
                key={c.id}
                onClick={() => { setCategory(c.id); if (postcode.trim()) runSearch(c.id); }}
                className="px-4 py-2.5 rounded-full whitespace-nowrap text-sm font-bold transition-all"
                style={{
                  background: category === c.id ? 'linear-gradient(135deg, #B89762, #C29D52)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${category === c.id ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
                  color: 'white',
                }}
                data-testid={`cat-${c.id}`}
              >
                <span className="mr-1.5">{c.emoji}</span>{c.label}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10">
              <MapPin size={16} weight="bold" className="text-white/50" />
              <input
                value={postcode}
                onChange={e => setPostcode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && runSearch()}
                placeholder="Postcode (e.g. SW1A 1AA)"
                className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 text-sm"
                data-testid="postcode-input"
              />
              <select
                value={radius}
                onChange={e => setRadius(Number(e.target.value))}
                className="bg-transparent outline-none text-white/70 text-xs"
                data-testid="radius-select"
              >
                {[5, 10, 25, 50, 100].map(r => <option key={r} value={r} className="bg-[#0F1014]">{r} mi</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 sm:w-48">
              <CalendarBlank size={16} weight="bold" className="text-white/50" />
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="flex-1 bg-transparent outline-none text-white text-sm"
                data-testid="date-input"
              />
            </div>
            <button
              onClick={() => runSearch()}
              disabled={loading}
              className="px-6 py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="search-btn"
            >
              <MagnifyingGlass size={16} weight="bold" /> {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
          {date && (
            <p className="text-[11px] text-emerald-300/90 font-bold mt-2 flex items-center gap-1" data-testid="date-filter-hint">
              🟢 Showing suppliers available on {formatDateShort(date)}
            </p>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-5 py-8">
        {/* Fayre injection — surfaces the most relevant upcoming fayre for
           the active category. Renders only after a category is picked
           so it doesn't shout at first-time visitors. */}
        {fayreSuggestion && hasSearched && (
          <div className="mb-5">
            <FayreInlinePromo
              fayre={fayreSuggestion}
              variant="banner"
              headline="🎪 Happening soon"
              testId="directory-fayre-promo"
            />
          </div>
        )}

        {!hasSearched && (
          <div className="text-center py-16" data-testid="empty-prompt">
            <p className="text-white/40 text-sm">Pick a category and enter a postcode to search.</p>
          </div>
        )}
        {hasSearched && results.length === 0 && !loading && (
          <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5" data-testid="no-results">
            <p className="text-white/60 font-bold mb-1">No suppliers in range yet</p>
            <p className="text-white/30 text-sm">Try widening your radius or a different category. We're onboarding new suppliers every day.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {results.map(s => (
            <div
              key={s.id}
              className="text-left flex gap-3 p-3 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 transition-all cursor-pointer"
              onClick={() => navigate(`/suppliers/${s.id}`)}
              data-testid={`supplier-card-${s.id}`}
            >
              <div
                className="w-20 h-20 rounded-lg bg-cover bg-center shrink-0"
                style={{
                  background: s.images?.[0]
                    ? `url(${s.images[0]}) center/cover`
                    : 'linear-gradient(135deg, rgba(108,85,224,0.3), rgba(194,157,82,0.2))',
                }}
              >
                {!s.images?.[0] && (
                  <div className="w-full h-full flex items-center justify-center text-white/30 text-2xl">
                    {categories.find(c => c.id === s.category)?.emoji || '🎯'}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm leading-tight truncate">{s.business_name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <p className="text-white/40 text-xs flex items-center gap-1">
                    <MapPin size={10} weight="bold" /> {s.city || s.postcode} · {s.distance_miles}mi
                  </p>
                  <ConfidenceBadge conf={s.availability_confidence} />
                  {s.is_verified && (
                    <span
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-emerald-400/15 border border-emerald-400/40 text-emerald-200"
                      data-testid={`card-verified-${s.id}`}
                      title="PAT + PLI on file and in-date"
                    >
                      ✓ Verified
                    </span>
                  )}
                </div>
                {(s.badges || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid="card-badges">
                    {s.badges.map((b) => <Badge key={b} kind={b} />)}
                  </div>
                )}
                <CardSignals signals={s.profile_signals} sid={s.id} />
                {s.description && (
                  <p className="text-white/50 text-xs mt-1.5 line-clamp-2">{s.description}</p>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-[#C29D52] text-xs font-bold">
                    <ChatCircle size={12} weight="fill" /> Message
                    <CaretRight size={10} weight="bold" />
                  </div>
                  {s.bookings_count > 0 && (
                    <p className="text-white/40 text-[10px] font-bold">
                      Booked {s.bookings_count}×
                    </p>
                  )}
                </div>
                {s.is_instant_bookable && (
                  <button
                    onClick={(e) => openInstant(e, s)}
                    className="mt-2 w-full py-2 rounded-lg text-[11px] font-black text-white flex items-center justify-center gap-1.5 hover:brightness-110 transition-all"
                    style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                    data-testid={`book-instantly-${s.id}`}
                  >
                    <Lightning size={12} weight="fill" /> Book Instantly
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {instantTarget && (
        <InstantBookingModal
          supplier={instantTarget}
          date={date}
          location={postcode}
          eventId={searchParams.get('event_id') || null}
          onClose={() => setInstantTarget(null)}
          onOpenChat={(chatId) => navigate(`/chat/${chatId}`)}
        />
      )}
    </div>
  );
};

const BADGE_CONFIG = {
  available_on_date: { label: 'Available on your date', icon: '🟢', cls: 'bg-emerald-400/15 text-emerald-200 border-emerald-400/30' },
  fast_responder: { label: 'Fast Responder', icon: '⚡', cls: 'bg-[#B89762]/40/15 text-[#C29D52]/85 border-[#B89762]/45/30' },
  highly_booked:  { label: 'Highly Booked', icon: '🏆', cls: 'bg-emerald-400/15 text-emerald-200 border-emerald-400/30' },
  popular:        { label: 'Popular', icon: '🔥', cls: 'bg-amber-400/15 text-amber-200 border-amber-400/30' },
};

const Badge = ({ kind }) => {
  const c = BADGE_CONFIG[kind];
  if (!c) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black border ${c.cls}`}
      data-testid={`badge-${kind}`}
    >
      <span>{c.icon}</span> {c.label}
    </span>
  );
};

// Availability confidence badge — customer-facing trust signal.
// Three labels mapping to server-side bands. We never show a raw % here —
// the wording is the verdict, the dot is the tone. Omitted entirely when
// score is 0 (the supplier hasn't set enough availability for us to signal
// anything reliable — negative framing avoided).
const CONFIDENCE_BADGE = {
  highly_reliable: { dot: '🟢', label: 'Highly reliable',        cls: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10' },
  getting_there:   { dot: '🔵', label: 'Getting there',          cls: 'text-sky-300 border-sky-400/30 bg-sky-500/10' },
  limited:         { dot: '🟠', label: 'Limited availability',   cls: 'text-amber-300 border-amber-400/30 bg-amber-500/10' },
};

const ConfidenceBadge = ({ conf }) => {
  if (!conf || !conf.label || !CONFIDENCE_BADGE[conf.label]) return null;
  const cfg = CONFIDENCE_BADGE[conf.label];
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${cfg.cls}`}
      data-testid={`confidence-badge-${conf.label}`}
      aria-label={cfg.label}
    >
      <span className="text-[9px]">{cfg.dot}</span> {cfg.label}
    </span>
  );
};

function formatDateShort(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

// ── Trust chips on search cards ─────────────────────────────────────
// Small, honest, scannable signals that reduce hesitation before click.
// Only render when the backend is confident (nullable fields are
// omitted, negative framing never shown).
const REPLY_CHIP = {
  within_15m: 'Replies within 15 mins',
  within_1h:  'Replies within 1 hour',
  same_day:   'Replies same day',
};

const CardSignals = ({ signals, sid }) => {
  if (!signals) return null;
  const chips = [];
  if (signals.reply_speed_bucket && REPLY_CHIP[signals.reply_speed_bucket]) {
    chips.push({
      key: `reply-${signals.reply_speed_bucket}`,
      icon: <Lightning size={9} weight="fill" />,
      label: REPLY_CHIP[signals.reply_speed_bucket],
      cls: 'bg-[#B89762]/40/10 text-[#C29D52]/85 border-[#B89762]/45/25',
      testid: `card-signal-reply-${sid}`,
    });
  }
  if (signals.has_certificates) {
    chips.push({
      key: 'docs-ready',
      icon: <FileText size={9} weight="fill" />,
      label: 'Docs ready',
      cls: 'bg-[#B89762]/30/5 text-[#C29D52]/85 border-[#B89762]/40/25',
      testid: `card-signal-docs-${sid}`,
    });
  }
  if (signals.bookings_this_week > 0) {
    chips.push({
      key: 'bookings-week',
      icon: <span className="text-[9px]">🔥</span>,
      label: `${signals.bookings_this_week} booked this week`,
      cls: 'bg-amber-400/10 text-amber-200 border-amber-400/25',
      testid: `card-signal-bookings-${sid}`,
    });
  }
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`card-signals-${sid}`}>
      {chips.map((ch) => (
        <span
          key={ch.key}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${ch.cls}`}
          data-testid={ch.testid}
        >
          {ch.icon} {ch.label}
        </span>
      ))}
    </div>
  );
};

export default SuppliersDirectory;
